import { useState } from "react";
import { CalendarClockIcon, RepeatIcon } from "lucide-react";

import { Column, EmptyState, ErrorText, LoadingIndicator } from "@/components";
import { relative } from "@/components/tools/assistant/format";
import { Chip, Tag, Why } from "@/components/tools/assistant/shared";
import {
  accentFor,
  DEFERRAL_REASON,
  MESSAGE_KIND,
  SCHEDULE_ACTION,
  SCHEDULE_SOURCE,
  SCHEDULE_STATUS,
} from "@/components/tools/assistant/vocab";
import { Button } from "@/components/ui/button";

import { useCancelAssistantSchedule } from "@/hooks/mutations/useCancelAssistantSchedule";
import { useGetAssistantSchedules } from "@/hooks/queries/useGetAssistantAdmin";
import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { AssistantScheduleRow } from "@/types";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const STATUSES = ["ACTIVE", "DONE", "CANCELED"] as const;
const SOURCES = ["STAFFER", "AGENT", "SYSTEM"] as const;

const describeRecurrence = (r: AssistantScheduleRow["recurrence"]) => {
  if (!r) return null;
  if (r.frequency === "daily") return `Daily at ${r.time}`;
  const days = (r.weekdays ?? []).map((d) => WEEKDAYS[d]).join(", ");
  return `Weekly ${days} at ${r.time}`;
};

export const AssistantSchedulesTab = ({
  onOpenThread,
}: {
  onOpenThread: (threadId: string) => void;
}) => {
  const [status, setStatus] = useState<(typeof STATUSES)[number]>("ACTIVE");
  const [source, setSource] = useState<(typeof SOURCES)[number] | null>(null);
  const { data: schedules, status: queryStatus } = useGetAssistantSchedules({});

  if (queryStatus === "pending") return <LoadingIndicator variant="medium" />;
  if (queryStatus === "error")
    return <ErrorText errorMessage="Error loading schedules." />;

  const shown = schedules.filter(
    (s) => s.status === status && (!source || s.source === source),
  );

  return (
    <Column className="min-h-0 flex-1 gap-4">
      <div className="flex flex-wrap items-center gap-2">
        {STATUSES.map((s) => (
          <FilterChip
            active={status === s}
            key={s}
            onClick={() => {
              setStatus(s);
            }}
          >
            <Chip
              count={schedules.filter((x) => x.status === s).length}
              def={SCHEDULE_STATUS[s]}
            />
          </FilterChip>
        ))}
        <span className="mx-1 h-5 w-px bg-slate-200" />
        {SOURCES.map((s) => (
          <FilterChip
            active={source === s}
            key={s}
            onClick={() => {
              setSource(source === s ? null : s);
            }}
          >
            <Chip
              count={
                schedules.filter((x) => x.status === status && x.source === s)
                  .length
              }
              def={SCHEDULE_SOURCE[s]}
            />
          </FilterChip>
        ))}
      </div>
      {shown.length === 0 ? (
        <EmptyState
          description="Reminders, follow-ups and held nudges show up here."
          Icon={CalendarClockIcon}
          title="Nothing here"
        />
      ) : (
        <Column className="gap-3 overflow-auto pb-6">
          {shown.map((s) => (
            <ScheduleCard key={s.id} onOpenThread={onOpenThread} schedule={s} />
          ))}
        </Column>
      )}
    </Column>
  );
};

const FilterChip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    className={cn(
      "rounded-full ring-offset-2 transition",
      active ? "ring-2 ring-slate-400" : "opacity-70 hover:opacity-100",
    )}
    onClick={onClick}
    type="button"
  >
    {children}
  </button>
);

const ScheduleCard = ({
  schedule: s,
  onOpenThread,
}: {
  schedule: AssistantScheduleRow;
  onOpenThread: (threadId: string) => void;
}) => {
  const { mutate: cancel, isPending } = useCancelAssistantSchedule();
  const action = SCHEDULE_ACTION[s.action] ?? {
    ...SCHEDULE_ACTION.remind,
    label: s.action,
  };
  const deferrals = s.payload.deferrals ?? [];
  const repeats = describeRecurrence(s.recurrence);

  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-lg border border-l-4 bg-white p-4",
        accentFor(action.tone),
        s.status === "CANCELED" && "opacity-60",
      )}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        {s.thread && (
          <button
            className="mr-1 rounded-full bg-slate-900 px-2 py-0.5 text-xs font-medium text-white hover:bg-slate-700"
            onClick={() => {
              if (s.thread) onOpenThread(s.thread.threadId);
            }}
            type="button"
          >
            {s.thread.staffer.name}
          </button>
        )}
        <Chip def={action} />
        <Chip def={SCHEDULE_SOURCE[s.source] ?? SCHEDULE_SOURCE.SYSTEM} />
        <Chip def={SCHEDULE_STATUS[s.status] ?? SCHEDULE_STATUS.ACTIVE} />
        {s.claimedAt && <Tag tone="amber">Running now</Tag>}
        {repeats && (
          <Tag tone="teal">
            <RepeatIcon className="mr-1 size-3" />
            {repeats}
          </Tag>
        )}
        <div className="ml-auto flex items-center gap-2">
          {s.status === "ACTIVE" && (
            <Button
              disabled={isPending}
              onClick={() => {
                if (window.confirm("Cancel this schedule?"))
                  cancel({ scheduleId: s.id });
              }}
              size="sm"
              variant="outline"
            >
              Cancel
            </Button>
          )}
        </div>
      </div>

      {s.payload.text && (
        <p className="text-sm font-medium text-slate-900">{s.payload.text}</p>
      )}
      {s.payload.why && (
        <p className="rounded-md bg-slate-50 px-2 py-1 text-xs text-slate-700">
          <span className="font-semibold">Why: </span>
          {s.payload.why}
        </p>
      )}

      {s.payload.held && s.payload.held.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="font-medium text-slate-500">Holding:</span>
          {(["nudge", "renudge", "digest"] as const).map((kind) => {
            const count = s.payload.held?.filter((h) => h.delivery === kind).length ?? 0;
            return count ? (
              <Chip count={count} def={MESSAGE_KIND[kind]} key={kind} />
            ) : null;
          })}
          {s.payload.plannedAt && (
            <span className="text-slate-500">
              · planned {dates.formatShortDateTime(s.payload.plannedAt)}
            </span>
          )}
        </div>
      )}
      {s.payload.signalKey && (
        <span className="truncate text-xs text-slate-400" title={s.payload.signalKey}>
          Email: {s.payload.signalKey}
        </span>
      )}

      <div className="flex flex-wrap gap-x-6 gap-y-1 border-t pt-2 text-xs text-slate-600">
        <Timing label="Next run" iso={s.nextRunAt} />
        <Timing label="Last ran" iso={s.lastRunAt} />
        <Timing label="Created" iso={s.createdAt} />
        {s.runCount > 0 && (
          <span>
            <span className="text-slate-400">Runs:</span> {s.runCount}
          </span>
        )}
      </div>

      {deferrals.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-500">
            Pushed back {deferrals.length}×
          </span>
          {[...deferrals].reverse().map((d, i) => (
            <Why def={DEFERRAL_REASON[d.reason]} key={`${d.at}-${i.toString()}`}>
              at {dates.formatShortDateTime(d.at)} → moved to{" "}
              <span className="font-semibold">
                {dates.formatShortDateTime(d.until)}
              </span>
            </Why>
          ))}
        </div>
      )}
    </div>
  );
};

const Timing = ({ label, iso }: { label: string; iso: string | null }) =>
  iso ? (
    <span>
      <span className="text-slate-400">{label}:</span>{" "}
      <span className="font-medium text-slate-800">
        {dates.formatShortDateTime(iso)}
      </span>{" "}
      <span className="text-slate-400">({relative(iso)})</span>
    </span>
  ) : null;
